﻿import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from "react-router-dom";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";
import { DateTime } from "luxon";
import { Autocomplete, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { useQueryClient } from "@tanstack/react-query";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { DataTableFilteredRequest, useDataTable } from "../../components/DataTable/TableHook";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { StoppageCalc } from "../../DTOs/Stoppage/StoppageCalc";
import { stoppagesMutations, wasQueued } from "../../offline/stoppagesMutations";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import NestedOperationsTable from "./NestedOperationsTable";
import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { operationLocationMutations } from "../../offline/operationLocationMutations";
import { engineHoursMutations } from "../../offline/engineHoursMutations";
import { filterCachedRows, matchesText } from "../../offline/filterCachedRows";
import { mirrorPlantStoppagesForStatus } from "../../offline/patchCachedStoppages";
import { plantStatusSignature, warmPlantStoppagesCache } from "../../offline/plantStoppagesCache";

const UNFILTERED_OPERATIONS = [{ key: 'Status', value: '' }, { key: 'MachineName', value: '' }];
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { Option } from "../../DTOs/Common/OptionResponse";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { TripleShift } from "../../types/Shift";
import { FilterConfig } from "../../components/DataTable/TableFiltersElement";
import { RootState } from "../../state/store";
import { Cancelled } from "../../types/ErrorTypes";
import { describeUpdatedMachines, isRelevantToViewedDate, parseUpdatedPlantIds, parseUpdatedShiftDate } from "./operationsLiveUpdate";

type StatusCellProps = {
  rowId: number;
  status: string;
  onClick: (id: number) => Promise<void>;
};

interface OperationsTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: {
    value: number,
    label: string,
    groupColor: string,
    stoppageType: string,
    downtimeTypeId: number
  }[];
  locationOptions: LocationDTO[];
  isLocationOptionsLoading: boolean;
  setRefreshFn?: (fn: () => void) => void;
}

const OperationsTable: React.FC<OperationsTableProps> = ({ selectedDate, selectedShift, stoppageTypeGroupOptions, locationOptions, isLocationOptionsLoading, setRefreshFn }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const refreshNestedTableRef = useRef<(() => void) | null>(null);
  const refreshNestedTable = useCallback(() => {
    if (refreshNestedTableRef.current) refreshNestedTableRef.current();
    else console.warn("Tried to refresh nested row before it was ready");
  }, []);
  const [tableFilters, setTableFilters] = useState<FilterConfig[]>([
    {
      key: 'Status',
      label: 'Statuses',
      type: 'dropdown',
      addAllValuesSelector: true,
      valueIndex: 0,
      possibleValues: ['UP', ...Array.from(new Set(stoppageTypeGroupOptions.map(o => o.stoppageType)))],
      placeholder: 'Filter by Status',
      value: ''
    },
    {
      key: 'MachineName',
      label: 'Machines',
      type: 'text',
      valueIndex: 1,
      placeholder: 'Filter by Machine Name',
      value: ''
    },
  ]);
  const { options: jobTypes } = useOptions<Option<number>, Option<number>>(
    ['references', 'jobTypes'],
    () => referencesAjax.getJobTypes(),
    o => o,
  );

  const updateEngineHours = useCallback(async (operationId: number, startHours: number, endHours: number) => {
    const response = await engineHoursMutations.updateEngineHours(operationId, startHours, endHours)

    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while updating the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      (response as any).errors?.forEach((error: any, index: number) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    if (!response.data) {
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(row => row.operationId === operationId
          ? new StoppageCalc({ ...row, startEngineHours: startHours, endEngineHours: endHours })
          : row),
      }));
      enqueueSnackbar("Hours saved offline; they will sync when you reconnect", { variant: 'info', autoHideDuration: 5000 })
      return true;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);

    enqueueSnackbar("Hours updated successfully", { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar])

  const updateLocation = useCallback(async (operationId: number, locationId: number) => {
    const response = await operationLocationMutations.updateOperationLocation(operationId, locationId);

    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while updating the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    if (!response.data) {
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(row => row.operationId === operationId ? { ...row, locationId } : row),
      }));
      enqueueSnackbar("Location saved offline; it will sync when you reconnect", { variant: 'info', autoHideDuration: 5000 })
      return true;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);
    enqueueSnackbar("Location updated successfully", { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar])

  const refreshPlant = useCallback(async (plantId: number) => {
    if (!isOnline) return true;

    const response = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, plantId)
    if (response instanceof ErrorResponse) {
      enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    updateRowSource((row) => row.plantId === response.plantId, response);
    return true;
  }, [selectedDate, selectedShift, enqueueSnackbar, isOnline]);

  const setAsAvailable = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.plantId || !stoppage.stoppageSubTypeId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid)
      return false;

    try {
      const response = await stoppagesMutations.updateStoppage(stoppage);

      if (!response.success && !response.data) {
        (response as any).errors?.forEach((error: any, index: number) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      enqueueSnackbar('Status updated', { variant: 'success', autoHideDuration: 3000 })

      if (wasQueued(response)) {
        mirrorPlantStoppagesForStatus(stoppage.plantId, selectedDate.toISODate()!, selectedShift, 'UP');
        mutateCachedResult(old => ({
          ...old,
          data: old.data.map(r => r.plantId === stoppage.plantId
            ? { ...r, status: 'UP', stoppageId: undefined, stoppageColourHub: '', statusDescription: '', currentStoppageTypeId: undefined, currentStoppageSubTypeId: undefined }
            : r
          )
        }));

        const activeStatusFilter = tableFilters.find(filter => filter.key === 'Status')?.value;
        if (activeStatusFilter && activeStatusFilter !== 'UP') {
          removeRowSource(row => row.plantId === stoppage.plantId);
        }
      } else {
        const updatedOperationResponse = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, stoppage.plantId)

        if (updatedOperationResponse instanceof ErrorResponse) {
          enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })

          return false;
        }

        updateRowSource((row) => row.plantId === updatedOperationResponse.plantId, updatedOperationResponse);
      }
    }
    catch (error) {
      if (error === Cancelled) return false;
      enqueueSnackbar("Error while refreshing the entry. Please refresh the page", { variant: 'error', autoHideDuration: 5000 })

      return false;
    }

    refreshNestedTable();
    return true;
  }, [enqueueSnackbar, selectedDate, selectedShift, refreshNestedTable, isOnline]);

  const getRows = useCallback(async (request: DataTableFilteredRequest) => {
    const filterValues = tableFilters.reduce((acc, cur) => {
      acc[cur.key] = cur.value ?? '';
      return acc;
    }, {} as Record<string, string>);
    const filters = { ...filterValues, pageNumber: 0, pageSize: 10 };

    const fromCache = () => {
      const status = filterValues['Status'] ?? '';
      const machineName = filterValues['MachineName'] ?? '';
      return filterCachedRows<StoppageCalc>(
        ['dataTable', 'operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift,
          UNFILTERED_OPERATIONS, 1, 5, null, 'asc', false, null],
        row => (!status || row.status === status) && matchesText(row.machineName, machineName),
        {
          field: request.sortField,
          direction: request.sortDirection,
          accessors: {
            Machine: row => row.machineName,
            Status: row => row.status,
          },
          fallback: row => row.machineName,
        },
      );
    }

    try {
      const result = await operationsAjax.getStoppageCalcsData(selectedDate, selectedShift, filters);
      return ErrorResponse.isErrorResponse(result) ? fromCache() : result;
    } catch (error) {
      if (error === Cancelled) throw error;
      return fromCache();
    }
  }, [selectedDate, selectedShift, tableFilters]);

  const translateRows = useCallback((r: StoppageCalc): TableRow => {
    return {
      id: r.plantId,
      values: [
        {
          name: 'Status',
          type: 'custom',
          value: r.status,
          tailwindClasses: `basis-[8%] justify-center`,
          render: (value) => {
            return (
              <span
                style={{
                  backgroundColor: r.status !== "UP" ? r.stoppageColourHub : ""
                }}
                className={`px-1 py-3 text-white font-bold h-full inline-flex w-full items-center justify-center ${r.status === "UP" ? 'bg-theme-success' : ""}`}>{value.value}</span>
            );
          },
        },
        {
          name: 'Toggle Available', type: 'custom', value: 'custom', tailwindClasses: 'basis-[6%]',
          render: (_, row) => r.status !== 'UP' && <StatusCell
            rowId={row.id}
            status={r.status}
            onClick={async () => {
              const now = DateTime.now();
              let merged = selectedDate.set({
                hour: now.hour,
                minute: now.minute,
                second: now.second,
                millisecond: now.millisecond
              });
              const startedAt = r.timeIn || now;
              if (merged < startedAt) merged = merged.plus({ days: 1 });
              await setAsAvailable(new StoppageDto({
                stoppageId: r.stoppageId,
                plantId: r.plantId,
                startTime: startedAt,
                endTime: merged,
                incident: false,
                stoppageTypeId: r.currentStoppageTypeId ?? 0,
                stoppageSubTypeId: r.currentStoppageSubTypeId ?? 0,
                locationId: r.locationId
              }));
            }}
          />
        },
        {
          name: 'Machine', sortable: true, type: 'string', value: r.machineName, tailwindClasses: 'basis-[8%] py-3',
          render: (val) => {
            switch (r.latestRowJobType) {
              case jobTypes.find(j => j.label === 'Production Digging')?.key:
                return (
                  <Link
                    to={`/production/show?diggingPatternId=${r.latestRowId}`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Blast Hole Drilling')?.key:
                return (
                  <Link
                    to={`/db/bh-drilling?drillingId=${r.latestRowId}`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Loader')?.key:
                return (
                  <Link
                    to={`/production/crushing`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              case jobTypes.find(j => j.label === 'Digging')?.key:
                return (
                  <Link
                    to={`/production/rehandle`}
                    target="_blank"
                    className="px-4 py-1 rounded bg-theme-info hover:bg-theme-info-hover transition text-white rounded-sm border-none"
                  >
                    <span>{val.value}</span>
                  </Link>
                )
              default:
                return (
                  <span>{val.value}</span>
                )
            }
          }
        },
        {
          name: 'Start Engine Hours', editable: true, type: 'decimal', value: r.startEngineHours, tailwindClasses: 'basis-[9%] py-3', disabled: isShiftLocked,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, newValue ?? 0, row.values.find(v => v.name === "End Engine Hours")?.value);
          }
        },
        {
          name: 'End Engine Hours', editable: true, type: 'decimal', value: r.endEngineHours, tailwindClasses: 'basis-[9%] py-3', disabled: isShiftLocked,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, row.values.find(v => v.name === "Start Engine Hours")?.value, newValue ?? 0);
          }
        },
        { name: 'Status Description', type: 'string', value: r.statusDescription, tailwindClasses: 'basis-[12%] py-3' },
        { name: 'Time In', type: 'string', value: r.timeIn ? (DateTime.isDateTime(r.timeIn) ? r.timeIn : DateTime.fromISO(r.timeIn as unknown as string, { setZone: true })).toLocal().toFormat('HH:mm') : undefined, tailwindClasses: 'basis-[7%] py-3' },
        {
          name: "Avail'",
          type: 'percentage',
          value: r.availabilityPercentage?.toFixed(2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Total Hours",
          type: 'integer',
          value: r.totalHours?.toFixed(2),
          tailwindClasses: 'basis-[4%] py-3'
        },
        {
          name: "Standby",
          type: 'integer',
          value: r.calculatedNod ?? "N/A",
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "NOD Difference",
          type: 'custom',
          value: r.calculatedNod ?? "N/A",
          tailwindClasses: 'basis-[6%] py-3',
          render: () => {
            if (!r.endEngineHours) return <span />;

            const diff = Math.abs((r.calculatedNod ?? 0) - (r.standby ?? 0));
            const isDanger = diff > 0.2;

            return (
              <span className={`px-2 py-1 rounded ${isDanger ? "bg-theme-danger text-white" : ""}`}>
                {diff.toFixed(2)}h
              </span>
            );
          }
        },
        {
          name: "UofA",
          type: 'percentage',
          value: r.utilizationOfAvailability?.toFixed(2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Location", type: 'custom', value: r.locationId, tailwindClasses: 'basis-[16%] py-3', render: (tableValue, row) =>
            <Autocomplete
              value={locationOptions.find(o => o.id === tableValue.value) ?? null}
              classes={{ root: "w-full transition" }}
              loading={isLocationOptionsLoading}
              disabled={isLocationOptionsLoading || isShiftLocked}
              options={locationOptions}
              getOptionLabel={(o) => o.name}
              onChange={(_, opt) => { updateLocation(r.operationId, opt?.id ?? 0) }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select Location"
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {isLocationOptionsLoading ? <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
        },
      ]
    }
  }, [isShiftLocked, selectedDate, setAsAvailable, jobTypes, updateEngineHours, locationOptions, isLocationOptionsLoading, updateLocation]);

  const warmedSignatureRef = useRef<string | null>(null);
  const prefetchStoppages = useCallback((entries: StoppageCalc[]) => {
    if (!isOnline) return;
    const dateIso = selectedDate.toISODate();
    if (!dateIso) return;

    const signature = plantStatusSignature(dateIso, selectedShift, entries);
    if (warmedSignatureRef.current === signature) return;
    warmedSignatureRef.current = signature;

    void warmPlantStoppagesCache(queryClient, selectedDate, selectedShift, entries);
  }, [queryClient, isOnline, selectedDate, selectedShift]);

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    refresh,
    updateRowSource,
    removeRowSource,
    mutateCachedResult,
  } = useDataTable<StoppageCalc>({
    queryKey: ['operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift, tableFilters.map(f => ({ key: f.key, value: f.value }))],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: 5,
    onSuccessfulFetch: prefetchStoppages,
  });

  const StatusCell: React.FC<StatusCellProps> = ({ rowId, status, onClick }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
      setIsLoading(true);
      await onClick(rowId);
      setIsLoading(false);
    };

    return (
      <span
        className="relative inline-flex w-full h-full items-center justify-center group cursor-pointer"
        onClick={handleClick}
      >
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-theme-text transition-opacity duration-[.2s]" />
        ) : status !== "UP" && (
          <>
            <FontAwesomeIcon
              icon={faSquare}
              size="2x"
              className="transition-opacity duration-[.2s] absolute opacity-100 group-hover:opacity-0"
            />
            <FontAwesomeIcon
              icon={faSquareCheck}
              size="2x"
              className="transition-opacity duration-[.2s] absolute opacity-0 group-hover:opacity-100 text-theme-success"
            />
          </>
        )}
      </span>
    );
  };

  useEffect(() => {
    setTableFilters([
      {
        key: 'Status',
        label: 'Statuses',
        type: 'dropdown',
        addAllValuesSelector: true,
        valueIndex: 0,
        possibleValues: ['UP', ...Array.from(new Set(stoppageTypeGroupOptions.map(o => o.stoppageType)))],
        placeholder: 'Filter by Status',
        value: ''
      },
      {
        key: 'MachineName',
        label: 'Machines',
        type: 'text',
        valueIndex: 2,
        placeholder: 'Filter by Machine Name',
        value: ''
      },
    ])

  }, [stoppageTypeGroupOptions]);

  const updateTableFilters = useCallback((filters: SetStateAction<FilterConfig[]>) => {
    setTableFilters(filters);
  }, []);

  useEffect(() => {
    if (setRefreshFn) setRefreshFn(() => refresh);
  }, [refresh, setRefreshFn]);

  useEffect(() => {
    if (!connection) return;

    const handleDataUpdated = async (jsonData: string) => {
      try {
        if (!isRelevantToViewedDate(parseUpdatedShiftDate(jsonData), selectedDate.toISODate())) return;

        const plantIdsToUpdate = parseUpdatedPlantIds(jsonData);
        if (plantIdsToUpdate.length === 0) return;

        const results = await Promise.all(plantIdsToUpdate.map(plantId =>
          operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, plantId)
        ));
        const updatedRowDtos = results.filter((dto): dto is StoppageCalc => !(dto instanceof ErrorResponse));

        if (updatedRowDtos.length !== results.length) {
          enqueueSnackbar("Live updates for this page have been interrupted. Please refresh the page", { variant: 'error', autoHideDuration: 5000 });
          return;
        }

        for (const updatedRowDto of updatedRowDtos) {
          updateRowSource(
            oldRow => oldRow.plantId === updatedRowDto.plantId,
            updatedRowDto
          );
        }
        refreshNestedTable();

        const message = describeUpdatedMachines(updatedRowDtos.map(dto => dto.machineName));
        if (message) enqueueSnackbar(message, { variant: 'info', autoHideDuration: 5000 });
      } catch (error) {
        if (error === Cancelled) return;
        console.error("Failed to parse DataUpdatedAsync JSON", error);
      }
    };

    connection.on("DataUpdatedAsync", handleDataUpdated);

    return () => {
      connection.off("DataUpdatedAsync", handleDataUpdated);
    };
  }, [connection, refreshNestedTable, selectedDate, selectedShift, enqueueSnackbar, updateRowSource]);

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      tableFilters={tableFilters}
      setTableFilters={updateTableFilters}
      minTableWidthPixels={1320}
      maxTableWidthPixels={1680}
      disableContainer={true}
      virtualizeOver={20}
      fillHeight
      expandedRowRenderer={row => {
        return (
          <NestedOperationsTable
            plantId={row.id}
            machineName={row.values.find(v => v.name === 'Machine')?.value}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            stoppageTypeGroupOptions={stoppageTypeGroupOptions}
            parentRowStatus={row.values.find(v => v.name === 'Status')?.value}
            refreshParent={refreshPlant}
            registerRefreshFn={(fn: () => void) => {
              refreshNestedTableRef.current = fn;
            }}
          />
        )
      }}
    />
  )
};

export default OperationsTable;
